import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter,
} from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { Check, Loader2, ArrowRight, Layers, Calendar } from "lucide-react";
import { useRecipes, useRecipeVersions } from "@/hooks/useRecipes";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export function SelectRecipeVersionModal({
  open,
  onOpenChange,
  projectId,
  onSelectVersion,
  className,
}) {
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [selectedVersionKey, setSelectedVersionKey] = useState(null);

  // 1. Fetch recipes for this project
  const { data: projectRecipesData, isLoading: isRecipesLoading } = useRecipes(
    { project: projectId, limit: 100 },
    { enabled: Boolean(open && projectId) }
  );

  const recipes = useMemo(() => {
    const list = projectRecipesData?.data || [];
    return Array.isArray(list) ? list : [];
  }, [projectRecipesData]);

  // Set default recipe
  useEffect(() => {
    if (recipes.length > 0) {
      if (!selectedRecipeId || !recipes.some((r) => r._id === selectedRecipeId)) {
        setSelectedRecipeId(recipes[0]._id);
      }
    }
  }, [recipes, selectedRecipeId]);

  const activeRecipe = useMemo(() => {
    return recipes.find((r) => r._id === selectedRecipeId) || recipes[0] || null;
  }, [recipes, selectedRecipeId]);

  // 2. Fetch versions for active recipe
  const { data: fetchedVersions = [], isLoading: isVersionsLoading } = useRecipeVersions(
    activeRecipe?._id,
    { enabled: Boolean(open && activeRecipe?._id) }
  );

  const allVersions = useMemo(() => {
    const map = new Map();

    // 1. Add from fetchedVersions of active/latest recipe
    if (Array.isArray(fetchedVersions)) {
      fetchedVersions.forEach((v) => {
        const vNum = Number(v.version ?? 0);
        const key = v._id || `v-${vNum}`;
        if (!map.has(key)) {
          map.set(key, { ...v, version: vNum, _id: v._id || key, parentRecipe: activeRecipe });
        }
      });
    }

    // 2. Add from all recipes & their versions
    recipes.forEach((r) => {
      if (Array.isArray(r.versions) && r.versions.length > 0) {
        r.versions.forEach((v) => {
          const vNum = Number(v.version ?? 0);
          const key = v._id || `v-${vNum}`;
          if (!map.has(key)) {
            map.set(key, { ...v, version: vNum, _id: v._id || key, parentRecipe: r });
          }
        });
      }
      const rNum = Number(r.version ?? 0);
      const rKey = r._id || `v-${rNum}`;
      if (!map.has(rKey)) {
        map.set(rKey, { ...r, version: rNum, _id: r._id || rKey, parentRecipe: r });
      }
    });

    return Array.from(map.values()).sort(
      (a, b) => Number(b.version ?? 0) - Number(a.version ?? 0)
    );
  }, [fetchedVersions, recipes, activeRecipe]);

  // Set default selected version
  useEffect(() => {
    if (allVersions.length > 0) {
      if (!selectedVersionKey || !allVersions.some((v) => (v._id || `v-${v.version}`) === selectedVersionKey)) {
        const first = allVersions[0];
        setSelectedVersionKey(first._id || `v-${first.version}`);
      }
    }
  }, [allVersions, selectedVersionKey]);

  const selectedVersion = useMemo(() => {
    return (
      allVersions.find((v) => (v._id || `v-${v.version}`) === selectedVersionKey) ||
      allVersions[0] ||
      null
    );
  }, [allVersions, selectedVersionKey]);

  const handleProceed = () => {
    if (selectedVersion) {
      onSelectVersion?.(selectedVersion, selectedVersion.parentRecipe || activeRecipe);
      onOpenChange(false);
    }
  };

  const isLoading = isRecipesLoading || (isVersionsLoading && allVersions.length === 0);

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        className={cn(
          "max-w-[560px] w-full p-6 rounded-3xl bg-white dark:bg-[#0D0B14] border border-[#EEEBF4] dark:border-primary/40 shadow-2xl",
          className
        )}
      >
        <ModalHeader className="pb-4 border-b border-[#EEEBF4] dark:border-primary/30">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-purple-50 dark:bg-primary/20 text-[#4B208B] dark:text-purple-300 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <ModalTitle className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">
                Select Recipe Version
              </ModalTitle>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Choose the recipe version you want to prepare sample for
              </p>
            </div>
          </div>
        </ModalHeader>

        {/* Versions List */}
        <div className="py-4 max-h-[340px] overflow-y-auto custom-scrollbar space-y-2.5">
          {isLoading ? (
            <div className="py-12 flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin text-[#4B208B] dark:text-purple-400" />
              <span className="text-xs font-medium">Loading recipe versions...</span>
            </div>
          ) : allVersions.length === 0 ? (
            <div className="py-12 flex flex-col items-center justify-center text-center px-4">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                No recipe versions found
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Please create a recipe version in Application Recipes first.
              </p>
            </div>
          ) : (
            allVersions.map((v) => {
              const vNum = Number(v.version ?? 0);
              const numStr = String(vNum + 1).padStart(2, "0");
              const key = v._id || `v-${vNum}`;
              const isSelected = key === selectedVersionKey;
              const isFinalized = ["final", "approved", "finalized"].includes(
                String(v.recipeStatus || "").toLowerCase()
              );
              const dateDisplay = formatDate(v.createdAt || activeRecipe?.createdAt);
              const codeDisplay = v.recipeCode || activeRecipe?.recipeCode || "-";
              const formatType = v.recipeFormat || v.recipeType || activeRecipe?.recipeFormat || activeRecipe?.recipeType || "Bakery";

              return (
                <div
                  key={key}
                  onClick={() => setSelectedVersionKey(key)}
                  onDoubleClick={handleProceed}
                  className={cn(
                    "p-3.5 rounded-2xl border transition-all cursor-pointer flex items-center justify-between gap-3",
                    isSelected
                      ? "border-[#4B208B] bg-purple-50/60 dark:bg-primary/20 shadow-xs ring-1 ring-[#4B208B]"
                      : "border-[#EEEBF4] dark:border-primary/30 bg-[#FCFBFD] dark:bg-[#121019] hover:bg-[#F7F5FA] dark:hover:bg-primary/10"
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Radio indicator */}
                    <div
                      className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all",
                        isSelected
                          ? "bg-[#4B208B] text-white shadow-xs"
                          : "border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-black/30"
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                    </div>

                    {/* Version Badge & Info */}
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2.5 py-0.5 rounded-lg bg-[#4B208B] text-white font-extrabold text-[11px] tracking-wider uppercase shrink-0">
                          VERSION {numStr}
                        </span>
                        {codeDisplay !== "-" && (
                          <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                            {codeDisplay}
                          </span>
                        )}
                        <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 capitalize px-2 py-0.5 rounded-md bg-gray-100 dark:bg-white/5">
                          {formatType}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          {dateDisplay}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="shrink-0">
                    {isFinalized ? (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                        Approved
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                        In Development
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <ModalFooter className="pt-3 border-t border-[#EEEBF4] dark:border-primary/30 flex items-center justify-end gap-2.5">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-xl text-xs font-semibold cursor-pointer border-[#EEEBF4] dark:border-primary/40 hover:bg-gray-100 dark:hover:bg-primary/20"
          >
            Cancel
          </Button>

          <Button
            type="button"
            onClick={handleProceed}
            disabled={!selectedVersion || allVersions.length === 0}
            className="px-5 py-2 rounded-xl text-xs font-bold bg-[#4B208B] hover:bg-[#3E1B77] text-white flex items-center gap-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
          >
            <span>Proceed to Sample</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
